import { type ReactNode, useCallback } from 'react';
import type { Action, Breakpoint, Filter, PlainAction } from '@root/@types/types';
import type { RankingInfo } from '@tanstack/match-sorter-utils';
import type { QueryStatus } from '@tanstack/react-query';
import type {
  ColumnFiltersState,
  ColumnPinningState,
  FilterFn,
  RowData,
  SortingState,
  VisibilityState,
} from '@tanstack/react-table';

import { TableContainer } from './components/TableContainer';
import { useDataTable } from './hooks/useDataTable';

interface ReactTableProps {
  actions?: Action[];
  columns: any[];
  columnVisibility?: VisibilityState;
  columnPinning?: ColumnPinningState;
  createEntityAction?: PlainAction;
  enableColumnFilters?: boolean;
  entityName: string;
  expandableData?: any[];
  filteredData: any[];
  filtersBarWrapperClassName?: string;
  initialFilters?: ColumnFiltersState;
  initialSorting?: SortingState;
  useQueryBuilder?: boolean;
  queryBuilderHeaderSlot?: ReactNode;
  queryBuilderFooterSlot?: ReactNode;
  isEmpty?: boolean;
  isFetching?: boolean;
  isLoading?: boolean;
  renderCustomElements?: (
    isGroupByParent: boolean,
    setIsGroupByParent: (isGroupByParent: boolean) => void,
  ) => () => ReactNode;
  status: QueryStatus;
  tableClasses?: string;
  rowCount: number;
  searchTerm: string;
  handleSearchChange: (searchTerm: string) => void;
  fetchNextPage: () => void;
  totalFetched: number;
  hasNextPage: boolean;
  pageSize?: number;
}

declare module '@tanstack/react-table' {
  // eslint-disable-next-line unused-imports/no-unused-vars
  interface ColumnMeta<TData extends RowData, TValue> {
    tooltip?: boolean;
    filter?: Partial<Filter> & {
      order?: number;
      isSingleSelection?: boolean;
    };
    dataCellClassName?: string;
    headingCellClassName?: string;
    headingLinkClassName?: string;
    isStandaloneDataCell?: boolean;
    showAboveBreakpoint?: Breakpoint;
  }
  interface FilterFns {
    fuzzy: FilterFn<unknown>;
  }
  interface FilterMeta {
    itemRank: RankingInfo;
  }
}

export const ReactTable = ({
  actions = [],
  columnPinning = {},
  columns,
  columnVisibility = {},
  createEntityAction,
  enableColumnFilters = true,
  entityName,
  expandableData = [],
  filteredData,
  filtersBarWrapperClassName,
  initialFilters,
  initialSorting,
  useQueryBuilder = false,
  queryBuilderHeaderSlot,
  queryBuilderFooterSlot,
  isEmpty,
  isFetching = false,
  isLoading = false,
  renderCustomElements,
  status,
  tableClasses,
  rowCount,
  searchTerm,
  handleSearchChange,
  fetchNextPage,
  totalFetched,
  hasNextPage,
  pageSize,
}: ReactTableProps) => {
  'use memo';

  //called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
  const fetchMoreOnBottomReached = useCallback(
    (containerRefElement?: HTMLDivElement | null) => {
      if (containerRefElement) {
        const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
        //once the user has scrolled within 500px of the bottom of the table, fetch more data if we can
        if (scrollHeight - scrollTop - clientHeight < 500 && hasNextPage && !isFetching && totalFetched < rowCount) {
          fetchNextPage();
        }
      }
    },
    [fetchNextPage, isFetching, totalFetched, rowCount, hasNextPage],
  );

  const tableApi = useDataTable({
    columnPinning: isEmpty ? undefined : columnPinning,
    columns,
    columnVisibility,
    data: filteredData,
    createEntityAction,
    enableColumnFilters,
    entityName,
    expandableData,
    initialFilters,
    initialSorting,
    isEmpty,
    isFetching,
    isLoading,
    renderCustomElements,
    status,
    rowCount,
  });

  const isTableInitialized = tableApi.meta.isInitialized;
  const isTableContainerLoading = isLoading || !isTableInitialized;

  return (
    <TableContainer
      actions={actions}
      className={tableClasses}
      enableColumnFilters={enableColumnFilters}
      enableColumnVisibility={true}
      enableGlobalSearch={true}
      entityName={entityName}
      filtersBarWrapperClassName={filtersBarWrapperClassName}
      isEmpty={isEmpty}
      isLoading={isTableContainerLoading}
      useQueryBuilder={useQueryBuilder}
      queryBuilderHeaderSlot={queryBuilderHeaderSlot}
      queryBuilderFooterSlot={queryBuilderFooterSlot}
      renderCustomElements={renderCustomElements}
      tableApi={tableApi}
      totalResults={rowCount}
      searchTerm={searchTerm}
      handleSearchChange={handleSearchChange}
      fetchMoreOnBottomReached={fetchMoreOnBottomReached}
      isFetching={isFetching}
      pageSize={pageSize}
    />
  );
};
